import { formatAud } from '@/pages/blueprint-lite/utils/formatters';

import type { CapitalGainsProjection } from '../../../assessment/types';
import s from '../styles/dashboard.module.css';

interface Props {
  projection: CapitalGainsProjection;
  purchasePrice: number;
}

const compactAud = new Intl.NumberFormat('en-AU', {
  notation: 'compact',
  maximumFractionDigits: 1,
  style: 'currency',
  currency: 'AUD',
  currencyDisplay: 'narrowSymbol',
});

const X_POS = [16.67, 50, 83.33] as const;
const Y_TOP = 14;
const Y_BOTTOM = 86;

const CapitalGainsCard = ({ projection, purchasePrice }: Props) => {
  const values = [
    purchasePrice,
    projection.yearFive,
    projection.yearSeven,
  ] as const;
  const vmin = Math.min(...values);
  const vmax = Math.max(...values);
  const range = vmax - vmin || 1;
  const yNorm = (v: number) =>
    Y_BOTTOM - ((v - vmin) / range) * (Y_BOTTOM - Y_TOP);

  const pts = X_POS.map((x, i) => ({ x, y: yNorm(values[i]) }));
  const [p0, p1, p2] = pts;

  // Smooth Catmull-Rom → cubic Bézier through three points.
  const c1x = p0.x + (p1.x - p0.x) / 3;
  const c1y = p0.y + (p1.y - p0.y) / 3;
  const c2x = p1.x - (p2.x - p0.x) / 6;
  const c2y = p1.y - (p2.y - p0.y) / 6;
  const c3x = p1.x + (p2.x - p0.x) / 6;
  const c3y = p1.y + (p2.y - p0.y) / 6;
  const c4x = p2.x - (p2.x - p1.x) / 3;
  const c4y = p2.y - (p2.y - p1.y) / 3;

  const curvePath =
    `M ${p0.x} ${p0.y}` +
    ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p1.x} ${p1.y}` +
    ` C ${c3x} ${c3y}, ${c4x} ${c4y}, ${p2.x} ${p2.y}`;
  const areaPath = `${curvePath} L ${p2.x} 100 L ${p0.x} 100 Z`;

  const delta5 = projection.yearFive - purchasePrice;
  const delta7 = projection.yearSeven - projection.yearFive;

  return (
    <>
      <p className={s.projectionOutlook}>{projection.stateOutlook}</p>

      <div className={s.chart} aria-hidden="true">
        <svg
          className={s.chartSvg}
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="growth-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#003862" stopOpacity="0.18" />
              <stop offset="100%" stopColor="#003862" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={areaPath} fill="url(#growth-fill)" />
          <path
            d={curvePath}
            fill="none"
            stroke="#003862"
            strokeWidth="2"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
        <div className={s.chartBaseline} />
        {pts.map((p, i) => (
          <span
            key={i}
            className={s.chartDot}
            style={{ left: `${p.x}%`, top: `${p.y}%` }}
          />
        ))}
      </div>

      <div className={s.timeline}>
        <div className={s.timelineCell}>
          <div className={s.timelineYear}>Today</div>
          <div className={s.timelineValue}>{formatAud(purchasePrice)}</div>
          <div className={s.timelineRate}>Acquisition</div>
        </div>
        <div className={s.timelineCell}>
          <div className={s.timelineYear}>Year 5</div>
          <div className={s.timelineValue}>{formatAud(projection.yearFive)}</div>
          <div className={s.timelineDelta}>
            {compactAud.format(delta5)} over 5 yrs
          </div>
        </div>
        <div className={s.timelineCell}>
          <div className={s.timelineYear}>Year 7</div>
          <div className={s.timelineValue}>{formatAud(projection.yearSeven)}</div>
          <div className={s.timelineDelta}>
            {compactAud.format(delta7)} over 2 yrs
          </div>
        </div>
      </div>

      <p className={s.projectionNote}>{projection.equityNote}</p>

      <div className={s.projectionCgt}>
        <span className={s.projectionCgtLabel}>CGT strategy</span>
        <p className={s.projectionCgtBody}>{projection.cgtPlanning}</p>
      </div>

      {projection.considerations.length > 0 && (
        <>
          <span className={s.projectionSub}>Considerations</span>
          <ul className={s.projectionConsList}>
            {projection.considerations.map((c, i) => (
              <li key={i}>{c}</li>
            ))}
          </ul>
        </>
      )}
    </>
  );
};

export default CapitalGainsCard;
